import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from '@gatsbyjs/reach-router';
import { Experiment, StoreToggle, useExperiment } from '@printdeal/feature-management/react';
import { Price } from '@printdeal/product-selector';
import { dispatchDataLayerEntry, dispatchFileCheckDataLayerEvent } from '../../helpers/dataLayerHelpers/productDataLayerHelper';
import { RootState } from '../../store';
// @ts-expect-error
import { selectors as customerSelectors } from '../../store/slices/customerSlice';
import { ISelection } from '../../types/selector/Selector';
import useSystemPages from '../../hooks/useSystemPages';
import { ProductInfo } from '../../types/design';
import { FileDeliverySource } from '../../types/file-delivery/FileDelivery';
import { messages } from '../../translations/login';

interface Props {
  mcpSKU: string,
  productKey: string,
  productVersion?: number,
  productName?: string,
  price?: Price
  mcpAttributes: string | ISelection,
  children: React.ReactNode,
  documentReferenceUrl?: string | null,
  reviewPath?: string,
  callbackUrl?: string,
  customizedProductId?: string | null,
  productCode?: string | null,
  defaultDesignName?: string | null,
  productInfo?: ProductInfo[],
  lineItemId?: string,
  reviewGroupId?: string,
  lineItemNumber?: string,
  orderId?: string,
  redirectUrl?: string,
  setStatus?: (status: 'opening-editor' | 'idle') => void,
  className?: string,
  editorOverrides?: string,
  source: FileDeliverySource,
  selectedServiceKeys?: string[],
  serviceKeys?: string,
  variantSku?: string,
  formId?: string,
  deliveryOptionId?: string,
}
interface Node {
  node_locale: string;
  title: string;
  content:{
    childMarkdownRemark: {
      html: string;
    }
  };
}
interface QueryResultProps {
  nodes?: Node[];
}
const query = graphql`{
  allContentfulSupportFaq(filter: {tags: {eq: "printq"}}) {
    nodes {
      node_locale
      content {
          childMarkdownRemark {
            html
          }
        }
      title
    }
  }
}
`;

const OpenPrintQForm = ({
  productKey,
  productVersion,
  deliveryOptionId,
  mcpSKU,
  productName,
  documentReferenceUrl,
  mcpAttributes,
  children,
  price,
  reviewPath,
  callbackUrl,
  productCode,
  customizedProductId,
  defaultDesignName,
  productInfo,
  lineItemId,
  reviewGroupId,
  lineItemNumber,
  orderId,
  redirectUrl,
  setStatus,
  className,
  editorOverrides,
  source,
  selectedServiceKeys,
  serviceKeys,
  variantSku,
  formId,
}: Props) => {
  const intl = useIntl();
  const { locale } = intl;

  // This experiment is for testing on custom printQ machine
  const {
    experiment: experimentForcePrintQ,
    isInitialized: isForcePrintQInitialized,
  } = useExperiment('pers_force_printq');
  const usePrintQTestMachine = isForcePrintQInitialized && experimentForcePrintQ.variation === 'variant';

  const { allContentfulSupportFaq = { nodes: [] } } = useStaticQuery<{
    allContentfulSupportFaq: QueryResultProps
  }>(query);

  const supportFaqs = allContentfulSupportFaq?.nodes?.map((node) =>
    locale === node?.node_locale && {
      title: node?.title,
      content: node?.content?.childMarkdownRemark?.html,
    });

  const location = useLocation();
  const authToken = useSelector<RootState, string | undefined>(({ auth }) => auth.token);
  const customerId = useSelector<RootState, string | undefined>((state) => customerSelectors.getCustomerId(state));
  const systemPages = useSystemPages('submitDesign', 'accountDashboard', 'registration', 'printq', 'lostPassword');
  const [submitDesign, accountDashboard, registrationPage, printq, lostPasswordPage] = systemPages;
  const registrationPagePath = new URL(`${window.location.origin}${registrationPage?.path}`);
  const lostPasswordPagePath = new URL(lostPasswordPage?.path, window.location.origin);

  const submitForm = () => {
    if (setStatus) {
      setStatus('opening-editor');
    }

    dispatchDataLayerEntry(
      'selector',
      'click',
      {
        'element-type': 'button',
        'element-content': 'submit_design',
      },
    );
    // add GA event for File Check service
    dispatchFileCheckDataLayerEvent(selectedServiceKeys);
  };

  const getRedirectUrl = () => {
    const path = `${orderId ? `${accountDashboard.path}/orders/${orderId}` : submitDesign.path}`;
    const url = new URL(`${window.location.origin}${path}`);

    if (lineItemNumber) {
      url.searchParams.set('lineItemNumber', lineItemNumber);
    }
    if (reviewGroupId) {
      url.searchParams.set('reviewGroupId', reviewGroupId);
    }
    if (lineItemId) {
      url.searchParams.set('lineItemId', lineItemId);
      url.searchParams.set('design_submitted', 'true');
    }

    if (productVersion) {
      url.searchParams.set('productVersion', productVersion.toString());
    }

    if (deliveryOptionId) {
      url.searchParams.set('deliveryOptionId', deliveryOptionId);
    }

    if (reviewPath) {
      return `${window.location.origin}${reviewPath}${window.location.search}`;
    }
    return url.href;
  };

  function getAction() {
    const printQUrl = localStorage.getItem('printQUrl');
    const path = usePrintQTestMachine && printQUrl ? printQUrl : `${printq.path}/mcp/computeEditorUrl`;
    return path;
  }

  return (
    <form id={formId} method="post" onSubmit={submitForm} action={getAction()} className={className}>
      <input readOnly type="hidden" name="store_code" value={`${process.env.GATSBY_PRINTQ_STORE_CODE}_${locale.substring(0, 2)}`} />
      <input readOnly type="hidden" name="redirect" value="1" />
      {productCode && <input readOnly type="hidden" name="changeProductSelectionUrl" value={productCode} />}
      <input readOnly type="hidden" name="productName" value={productName} />
      <input readOnly type="hidden" name="source" value={source} />

      <input readOnly type="hidden" name="designEndpoint" value={`${process.env.GATSBY_DESIGNS_ENDPOINT}/save`} />
      <input readOnly type="hidden" name="loginCustomerUrl" value={`${process.env.GATSBY_AUTH_ENDPOINT}/login`} />
      <input readOnly type="hidden" name="checkCustomerUrl" value={`${process.env.GATSBY_CUSTOMER_URL}/customer/getCustomer`} />
      {/* TODO:Remove this after clouldlab deployment */}
      <input readOnly type="hidden" name="convertConfigUrl" value={`${process.env.GATSBY_PRODUCT_ENDPOINT}/product/convert`} />

      <input readOnly type="hidden" name="productApi" value={`${process.env.GATSBY_PRODUCT_ENDPOINT}`} />

      <input readOnly type="hidden" name="success_url" value={redirectUrl || getRedirectUrl()} />
      <input readOnly type="hidden" name="cancel_url" value={location.href} />
      { price && (<input readOnly type="hidden" name="netPrice" value={((price.totalNet?.centAmount || 0) / 100).toFixed(2)} />)}
      {customerId && (<input readOnly type="hidden" name="customer" value={customerId} />)}
      <input readOnly type="hidden" name="locale" value={locale} />
      <input readOnly type="hidden" name="authToken" value={authToken} />
      <input readOnly type="hidden" name="mcpSku" value={mcpSKU} />
      <input readOnly type="hidden" name="variantSku" value={variantSku} />
      {customizedProductId && <input readOnly type="hidden" name="customizedProductId" value={customizedProductId} />}
      <input readOnly type="hidden" name="productKey" value={`${productKey}`} />
      { productVersion && <input readOnly type="hidden" name="productVersion" value={productVersion} /> }
      {/* @ts-expect-error Type of mcpAttributes is missing properties from type 'readonly string[]' of the value. */}
      <input readOnly type="hidden" name="mcpAttributes" value={mcpAttributes} />
      <StoreToggle id="drukwerkdeal-nl_printdeal-be">
        <StoreToggle.Enabled>
          { selectedServiceKeys && (<input readOnly type="hidden" name="selectedServiceKeys" value={JSON.stringify(selectedServiceKeys)} />)}
          { serviceKeys && (<input readOnly type="hidden" name="serviceKeys" value={JSON.stringify(serviceKeys.split(',')).toUpperCase()} />)}
        </StoreToggle.Enabled>
      </StoreToggle>
      <input readOnly type="hidden" name="FILE_CHECK_SERVICE_KEY" value={`${process.env.GATSBY_FILE_CHECK_SERVICE_KEY}`} />
      <input readOnly type="hidden" name="registrationPagePath" value={`${registrationPagePath}`} />
      <input readOnly type="hidden" name="lostPasswordPage" value={`${lostPasswordPagePath}`} />
      <input readOnly type="hidden" name="termsUrl" value={`${location.origin}${intl.formatMessage(messages.termsUrl)}`} />

      <input readOnly type="hidden" name="configUrl" value={`${process.env.GATSBY_CONFIGURATION_ENDPOINT}/getConfiguration`} />
      <input readOnly type="hidden" name="authTokenUrl" value={`${process.env.GATSBY_AUTH_ENDPOINT}`} />
      <input readOnly type="hidden" name="storeKey" value={`${process.env.GATSBY_STORE}`} />
      <input readOnly type="hidden" name="channel" value={`${process.env.GATSBY_CHANNEL}`} />
      <input readOnly type="hidden" name="editorOverrides" value={`${editorOverrides}`} />
      <input readOnly type="hidden" name="GATSBY_COOKIEBOT_CBID" value={`${process.env.GATSBY_COOKIEBOT_CBID}`} />

      <Experiment id="pers_exp867_offer_knowledge_center_in_tooling">
        <Experiment.Variant>
          <input readOnly type="hidden" name="supportFaqs" value={JSON.stringify(supportFaqs)} />
        </Experiment.Variant>
      </Experiment>

      <StoreToggle id="drukwerkdeal-nl">
        <Experiment id="brand_exp1658_re-branding_2024_v2">
          <Experiment.Variant>
            <input readOnly type="hidden" name="rebrandingExp" value="variant" />
          </Experiment.Variant>
        </Experiment>
      </StoreToggle>

      <input readOnly type="hidden" name="defaultDesignName" value={`${defaultDesignName} ${new Date().toLocaleString()}`} />
      { callbackUrl && (<input readOnly type="hidden" name="callbackUrl" value={callbackUrl} />)}
      { productInfo && (<input readOnly type="hidden" name="productInfo" value={JSON.stringify(productInfo)} />)}
      { documentReferenceUrl && (<input readOnly type="hidden" name="documentReference" value={documentReferenceUrl} />)}
      { children}
    </form>
  );
};

export default OpenPrintQForm;
